<template>
  <div class="org-chart-toolbar mb-1">
    <div class="org-chart-toolbar__bar d-flex align-items-center justify-content-end">
      <b-button
        variant="outline-primary"
        size="sm"
        class="org-chart-toolbar__filter-btn"
        :class="{ 'org-chart-toolbar__filter-btn--active': showFilters || activeFilterCount > 0 }"
        :aria-expanded="showFilters ? 'true' : 'false'"
        aria-controls="org-chart-filters-panel"
        @click="showFilters = !showFilters"
      >
        <feather-icon
          :icon="showFilters ? 'ChevronUpIcon' : 'FilterIcon'"
          size="14"
          class="mr-50"
        />
        Filters
        <b-badge
          v-if="activeFilterCount > 0"
          variant="primary"
          pill
          class="ml-50 org-chart-toolbar__badge"
        >
          {{ activeFilterCount }}
        </b-badge>
      </b-button>
    </div>

    <b-collapse
      id="org-chart-filters-panel"
      v-model="showFilters"
      class="org-chart-toolbar__collapse"
    >
      <b-card class="org-chart-toolbar__panel" body-class="py-75 px-1 px-md-2">
        <!-- Search -->
        <div class="org-chart-toolbar__section">
          <label class="org-chart-toolbar__label" for="org-chart-search">
            <feather-icon icon="SearchIcon" size="14" class="mr-50" />
            Search
          </label>
          <b-input-group size="sm" class="org-chart-toolbar__search">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" size="14" />
            </b-input-group-prepend>
            <b-form-input
              id="org-chart-search"
              :value="search"
              type="search"
              placeholder="Search name, emp id, role…"
              aria-label="Search organization chart"
              @input="$emit('update:search', $event)"
            />
            <b-input-group-append v-if="search">
              <b-button
                variant="outline-secondary"
                title="Clear search"
                aria-label="Clear search"
                @click="$emit('update:search', '')"
              >
                <feather-icon icon="XIcon" size="14" />
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>

        <!-- Tree -->
        <div class="org-chart-toolbar__section">
          <div class="org-chart-toolbar__label">
            <feather-icon icon="GitBranchIcon" size="14" class="mr-50" />
            Tree
          </div>
          <b-button-group size="sm" class="org-chart-toolbar__btn-group">
            <b-button
              variant="outline-secondary"
              title="Expand all"
              @click="$emit('expand-all')"
            >
              <feather-icon icon="Maximize2Icon" size="14" class="mr-25" />
              Expand
            </b-button>
            <b-button
              variant="outline-secondary"
              title="Collapse all"
              @click="$emit('collapse-all')"
            >
              <feather-icon icon="Minimize2Icon" size="14" class="mr-25" />
              Collapse
            </b-button>
          </b-button-group>
        </div>

        <!-- Zoom -->
        <div class="org-chart-toolbar__section">
          <div class="org-chart-toolbar__label">
            <feather-icon icon="ZoomInIcon" size="14" class="mr-50" />
            Zoom
          </div>
          <b-button-group size="sm" class="org-chart-toolbar__btn-group org-chart-toolbar__zoom">
            <b-button
              variant="outline-secondary"
              title="Zoom out"
              aria-label="Zoom out"
              @click="$emit('zoom-out')"
            >
              <feather-icon icon="ZoomOutIcon" size="14" />
            </b-button>
            <b-button
              variant="outline-secondary"
              disabled
              class="org-chart-toolbar__zoom-label"
              :title="`${zoomPercent}%`"
            >
              {{ zoomPercent }}%
            </b-button>
            <b-button
              variant="outline-secondary"
              title="Zoom in"
              aria-label="Zoom in"
              @click="$emit('zoom-in')"
            >
              <feather-icon icon="ZoomInIcon" size="14" />
            </b-button>
            <b-button
              variant="outline-secondary"
              title="Fit to screen"
              aria-label="Fit to screen"
              @click="$emit('fit-view')"
            >
              <feather-icon icon="MaximizeIcon" size="14" />
              <span class="d-none d-sm-inline ml-25">Fit</span>
            </b-button>
            <b-button
              variant="outline-secondary"
              title="Reset view"
              aria-label="Reset view"
              @click="$emit('reset-view')"
            >
              <feather-icon icon="RefreshCwIcon" size="14" />
              <span class="d-none d-sm-inline ml-25">Reset</span>
            </b-button>
          </b-button-group>
        </div>

        <!-- Display -->
        <div
          v-if="canMyTeam || canShowInactive"
          class="org-chart-toolbar__section org-chart-toolbar__section--last"
        >
          <div class="org-chart-toolbar__label">
            <feather-icon icon="SlidersIcon" size="14" class="mr-50" />
            Display
          </div>
          <div class="org-chart-toolbar__toggles">
            <b-form-checkbox
              v-if="canMyTeam"
              :checked="myTeamOnly"
              class="org-chart-toolbar__check"
              switch
              @change="$emit('update:myTeamOnly', $event)"
            >
              My team
            </b-form-checkbox>
            <b-form-checkbox
              v-if="canShowInactive"
              :checked="showInactive"
              class="org-chart-toolbar__check"
              switch
              @change="$emit('update:showInactive', $event)"
            >
              Inactive
            </b-form-checkbox>
          </div>
        </div>
      </b-card>
    </b-collapse>
  </div>
</template>

<script>
import {
  BCard,
  BCollapse,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend,
  BButton,
  BButtonGroup,
  BFormCheckbox,
  BBadge,
} from 'bootstrap-vue'

export default {
  name: 'OrgChartToolbar',
  components: {
    BCard,
    BCollapse,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BButton,
    BButtonGroup,
    BFormCheckbox,
    BBadge,
  },
  props: {
    search: { type: String, default: '' },
    scale: { type: Number, default: 1 },
    showInactive: { type: Boolean, default: false },
    myTeamOnly: { type: Boolean, default: false },
    canShowInactive: { type: Boolean, default: false },
    canMyTeam: { type: Boolean, default: false },
  },
  data() {
    return {
      showFilters: false,
    }
  },
  computed: {
    zoomPercent() {
      return Math.round(this.scale * 100)
    },
    activeFilterCount() {
      let count = 0
      if (String(this.search || '').trim()) count += 1
      if (this.myTeamOnly) count += 1
      if (this.showInactive) count += 1
      return count
    },
  },
}
</script>

<style lang="scss" scoped>
.org-chart-toolbar {
  &__bar {
    gap: 0.5rem;
  }

  &__filter-btn {
    font-weight: 500;

    &--active {
      border-color: #00ac99;
      color: #00ac99;
    }
  }

  &__badge {
    font-size: 0.65rem;
    vertical-align: middle;
  }

  &__panel {
    margin-top: 0.5rem;
    border: 1px solid #ebe9f1;
    box-shadow: none !important;
    border-radius: 0.428rem;
  }

  &__section {
    padding-bottom: 0.75rem;
    margin-bottom: 0.75rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);

    &--last {
      padding-bottom: 0;
      margin-bottom: 0;
      border-bottom: none;
    }
  }

  &__label {
    display: flex;
    align-items: center;
    font-size: 0.8rem;
    font-weight: 600;
    color: #5e5873;
    margin-bottom: 0.5rem;
    white-space: nowrap;

    .feather {
      color: #00ac99;
    }
  }

  &__search {
    max-width: 100%;
  }

  &__btn-group {
    flex-wrap: wrap;
  }

  &__zoom {
    flex-wrap: wrap;
  }

  &__zoom-label {
    min-width: 3.25rem;
    font-variant-numeric: tabular-nums;
    font-size: 0.75rem;
  }

  &__toggles {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem 1.25rem;
  }

  &__check {
    white-space: nowrap;
    font-size: 0.85rem;
    margin-bottom: 0;
  }
}

@media (max-width: 575.98px) {
  .org-chart-toolbar {
    &__btn-group {
      display: flex;
      width: 100%;

      .btn {
        flex: 1 1 auto;
      }
    }

    &__zoom {
      .btn:not(.org-chart-toolbar__zoom-label) {
        flex: 0 0 auto;
      }

      .org-chart-toolbar__zoom-label {
        flex: 1 1 auto;
      }
    }
  }
}
</style>
